import React, { useEffect, useMemo, useState } from 'react';
import Editor from './Editor';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getEntitiesById, getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { Button, Col, Dropdown, List, Menu, message, Popconfirm, Row } from 'antd';
import { DeleteOutlined, FileOutlined, PlusOutlined } from '@ant-design/icons';
import AIIcon from '../AIIcon';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import TemplateSelector from './Templates';
import { EmptyState } from '../empty_state/EmptyState';
import AIGenerateDocument from './AIGenerateDocument';
import {
	Document,
	DocumentContent,
	Documents,
	LocationDocumentsProps,
} from '../../types/documentTypes';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	canUserViewDocument,
	canUserDeleteDocument,
	getCompanyWideDocumentPermissions,
	getMergedDocumentPermissions,
	normalizePermissionsForAllRoles,
	canUserEditDocument,
} from '../../utils/LocationUtils';
import posthog from 'posthog-js';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import {
	locationDocumentsRestCrudThunksForBuyer,
	locationDocumentsRestCrudThunksForSupplier,
} from '../../thunks/documents_thunks';

const LocationDocuments: React.FC<any> = ({
	userLocation,
	updateDocument,
	companyConfig,
	currentUser,
	fetchLocationDocuments,
	createLocationDocument,
	deleteLocationDocument,
	documents,
	fetching,
	history,
	match,
	userType,
}) => {
	const [showTemplates, setShowTemplates] = useState(false);
	const [showAIDocument, setShowAIDocument] = useState(false);

	const locationId = match.params.id;
	const documentIdParam = match.params.documentId;
	const selectedDocumentId = documentIdParam ? parseInt(documentIdParam, 10) : null;

	const baseDocumentsPath = useMemo(
		() => `/${userType}/locations/detail/${locationId}/documents`,
		[userType, locationId]
	);

	const getLocationDocuments = () => {
		return fetchLocationDocuments()
			.then((allDocuments: Documents) => {
				if (allDocuments.length > 0) {
					history.replace(`${baseDocumentsPath}/${allDocuments[0].id}`);
				}
			})
			.catch((error) => {
				console.error('Error fetching documents:', error);
			});
	};

	useEffect(() => {
		getLocationDocuments();
	}, []);

	const createNewDocument = async (documentContent: DocumentContent) => {
		const hide = message.loading('Creating document...', 0);
		try {
			const userType = nullSafeGet('userType', currentUser);
			const companyWidePermissions = getCompanyWideDocumentPermissions(userType, companyConfig);
			const normalizedPermissions = normalizePermissionsForAllRoles(
				userType,
				companyWidePermissions
			);

			const documentToCreate: Document = {
				locationId: userLocation.id,
				document: documentContent,
			};

			if (userType === ROLE_TYPES.BUYER) {
				documentToCreate.buyerPermissions = normalizedPermissions;
			} else {
				documentToCreate.supplierPermissions = normalizedPermissions;
			}

			const newDocument = await createLocationDocument(documentToCreate);

			if (newDocument && newDocument.id) {
				await fetchLocationDocuments();
				history.push(`${baseDocumentsPath}/${newDocument.id}`);
			}
		} catch (error) {
			console.error('Failed to create document:', error);
			message.error('Failed to create document');
		} finally {
			setShowAIDocument(false);
			setShowTemplates(false);
			hide();
		}
	};

	const deleteDocument = (document: Document) => {
		deleteLocationDocument(document).then(() => {
			return getLocationDocuments();
		});
	};

	const addBlankDocument = () => {
		createNewDocument({ title: '', blocks: '' });
	};

	const selectedDocument: Document | undefined = useMemo(() => {
		const base = documents.find((document: Document) => document.id === selectedDocumentId);
		if (!base) {
			return undefined;
		}

		const existingMerged = nullSafeGet('mergedPermissions', base);
		if (existingMerged) {
			return base;
		}

		const userType = nullSafeGet('userType', currentUser);
		const mergedPermissions = getMergedDocumentPermissions(base, userType, companyConfig);

		return {
			...base,
			mergedPermissions,
		} as Document;
	}, [documents, selectedDocumentId, currentUser, companyConfig]);

	const canEditDocuments = canUserEditDocument(selectedDocument, currentUser, companyConfig);

	return (
		<Row className="h-full min-h-[70vh] bg-white">
			<Col span={8} style={{ borderRight: '1px solid #E5E5E5' }}>
				<div
					className="flex flex-row items-center justify-between px-6 py-4"
					style={{ borderBottom: '1px solid #E5E5E5' }}
				>
					<div className="h5medium">Documents ({documents.length})</div>
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS}>
						<div className="flex flex-row items-center justify-end">
							<Dropdown
								className="ml-4"
								overlay={
									<Menu>
										<Menu.Item key="pdf" onClick={addBlankDocument}>
											Start Blank
										</Menu.Item>
										<Menu.Item key="template" onClick={() => setShowTemplates(true)}>
											Use Template
										</Menu.Item>
										{posthog.isFeatureEnabled('wiki-withai') ? (
											<Menu.Item key="ai" icon={<AIIcon />} onClick={() => setShowAIDocument(true)}>
												Start with AI
											</Menu.Item>
										) : null}
									</Menu>
								}
								placement="bottomRight"
								trigger={['click']}
							>
								<Button type="primary" icon={<PlusOutlined />}>
									New
								</Button>
							</Dropdown>
						</div>
					</AccessPermissionChecker>
				</div>

				<div className="flex flex-col">
					<List
						loading={fetching}
						dataSource={documents}
						locale={{
							emptyText: (
								<EmptyState
									graphic={<FileOutlined className="text-2xl text-character-secondary" />}
									body={
										<span className="bodyregular text-character-secondary">
											You Haven’t Created Any Documents
										</span>
									}
								/>
							),
						}}
						renderItem={(document: Document) => (
							<List.Item
								className={` group cursor-pointer bg-neutral-1 px-6 py-1 hover:bg-neutral-2 ${
									selectedDocumentId === document.id ? 'bg-neutral-3' : ''
								}`}
								key={document.id}
								onClick={() => {
									if (document.id) {
										history.push(`${baseDocumentsPath}/${document.id}`);
									}
								}}
							>
								<div className="flex w-full flex-row items-baseline justify-between px-6 py-1">
									<span className="bodyregular">
										{nullSafeGet('document.title', document) || 'New Document'}
									</span>
									{canUserDeleteDocument(document, currentUser) ? (
										<div className="relative">
											<Popconfirm
												title="Are you sure you want to delete this document?"
												onConfirm={() => deleteDocument(document)}
											>
												<Button
													type="text"
													icon={<DeleteOutlined className="stroke-black" />}
													className=" opacity-0 transition-opacity duration-200 group-hover:opacity-100"
												/>
											</Popconfirm>
										</div>
									) : null}
								</div>
							</List.Item>
						)}
					/>
				</div>
			</Col>
			<Col span={16}>
				{nullSafeGet('length', documents) && selectedDocument ? (
					<Editor
						key={selectedDocumentId}
						document={nullSafeGet('document', selectedDocument)}
						fullDocument={selectedDocument}
						timeZoneId={nullSafeGet('timeZoneId', userLocation)}
						updateDocument={(documentData: Document) => {
							return updateDocument({
								id: selectedDocumentId,
								locationId: userLocation.id,
								...documentData,
							});
						}}
						editable={canEditDocuments}
					/>
				) : (
					<div className="flex h-full items-center justify-center">
						<EmptyState
							body={
								<span className="h4medium inline-block max-w-[440px] text-center">
									Keep important files, procedures, and location details organized by creating your
									first document.
								</span>
							}
						/>
					</div>
				)}
			</Col>
			{showTemplates ? (
				<TemplateSelector
					onSelectTemplate={(template: DocumentContent) => {
						createNewDocument(template);
					}}
					onCancel={() => setShowTemplates(false)}
				/>
			) : null}
			{showAIDocument ? (
				<AIGenerateDocument
					onGenerateDocument={(template: DocumentContent) => {
						createNewDocument(template);
					}}
					onCancel={() => setShowAIDocument(false)}
				/>
			) : null}
		</Row>
	);
};

const mapStateToProps = (state, ownProps) => ({
	userLocation: getEntityById(ownProps.match.params.id, state.locations),
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
	documents: getEntitiesById(
		nullSafeGet('documents.location_documents.recordsIds', state),
		nullSafeGet('documents.records', state)
	),
	fetching: nullSafeGet('documents.location_documents.fetching', state) || false,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateDocument: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationDocumentsRestCrudThunksForSupplier.update(entity)
				: locationDocumentsRestCrudThunksForBuyer.update(entity)
		),
	fetchLocationDocuments: () => {
		return dispatch((dispatch, getState) => {
			const state = getState();
			const currentUser = state.session.currentUser;
			const companyConfig = state.company_config.detail;

			const documentFilterAdaptor = (documents: Documents) => {
				const userType = nullSafeGet('userType', currentUser);

				return documents
					.filter((document: Document) => canUserViewDocument(document, currentUser, companyConfig))
					.map((document: Document) => {
						const mergedPermissions = getMergedDocumentPermissions(
							document,
							userType,
							companyConfig
						);
						return {
							...document,
							mergedPermissions,
						};
					});
			};

			return dispatch(
				ownProps.userType === ROLE_TYPES.SUPPLIER
					? locationDocumentsRestCrudThunksForSupplier.read(
							{ locationId: ownProps.match.params.id },
							'location_documents',
							null,
							null,
							null,
							false,
							documentFilterAdaptor
					  )
					: locationDocumentsRestCrudThunksForBuyer.read(
							{ locationId: ownProps.match.params.id },
							'location_documents',
							null,
							null,
							null,
							false,
							documentFilterAdaptor
					  )
			);
		});
	},
	createLocationDocument: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationDocumentsRestCrudThunksForSupplier.create(entity)
				: locationDocumentsRestCrudThunksForBuyer.create(entity)
		),
	deleteLocationDocument: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationDocumentsRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: locationDocumentsRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
});

export default withUserType(
	withRouter(withCurrentUser(connect(mapStateToProps, mapDispatchToProps)(LocationDocuments)))
);
